import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";
import { Pipeline, StepState, StepStatus } from "../../models/company";

interface IntegrationActivationPipelineProps {
  validation: Pipeline | null | undefined;
  hasPipeline: boolean;
  integrationName?: string;
  integrationStatus?: string;
  onStartPipeline?: (force: boolean) => void;
  isPipelineStarting?: boolean;
  readOnly?: boolean;
}

function IntegrationActivationPipeline({
  validation,
  hasPipeline,
  integrationName,
  integrationStatus,
  onStartPipeline,
  isPipelineStarting = false,
  readOnly = false,
}: IntegrationActivationPipelineProps) {
  const getStatusBadgeVariant = (status: StepStatus): string => {
    switch (status) {
      case "completed":
        return "success";
      case "in_progress":
        return "primary";
      case "failed":
        return "danger";
      case "blocked":
        return "warning";
      case "pending":
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status: StepStatus): string => {
    switch (status) {
      case "completed":
        return "bi-check-circle-fill";
      case "in_progress":
        return "bi-arrow-clockwise";
      case "failed":
        return "bi-x-circle-fill";
      case "blocked":
        return "bi-exclamation-triangle-fill";
      case "pending":
      default:
        return "bi-circle";
    }
  };

  const formatDuration = (startedAt: string | null, completedAt: string | null): string | null => {
    if (!startedAt) return null;

    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const durationMs = end.getTime() - start.getTime();

    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatDateTime = (dateTime: string | null): string => {
    if (!dateTime) return "N/A";
    const date = new Date(dateTime);
    return date.toLocaleString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const isCurrentStep = (step: StepState): boolean => {
    return step.step === validation?.current_step;
  };

  if (!validation) {
    return (
      <Card className="mb-4">
        <Card.Header className="bg-light">
          <h5 className="mb-0">
            <i className="bi bi-list-check me-2"></i>
            Activation Pipeline
          </h5>
        </Card.Header>
        <Card.Body className="text-center py-5">
          <div className="mb-3">
            <i className="bi bi-play-circle" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
          </div>
          <p className="text-muted mb-3">
            {hasPipeline
              ? "No activation pipeline is currently running for this integration."
              : `This ${integrationName} integration has no Activation Pipeline.`}
          </p>
          {hasPipeline && !readOnly && onStartPipeline && (
            <Button variant="primary" onClick={() => onStartPipeline(false)} disabled={isPipelineStarting}>
              {isPipelineStarting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Validating...
                </>
              ) : (
                <>
                  <i className="bi bi-play-fill me-2"></i>Start Activation Pipeline
                </>
              )}
            </Button>
          )}
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Header className="bg-light">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-list-check me-2"></i>
            Activation Pipeline
          </h5>
          {(validation.completed_at || validation.current_step === "pending_for_activation") && (
            <Badge bg="success">
              <i className="bi bi-check-circle me-1"></i>
              Completed
            </Badge>
          )}
        </div>
      </Card.Header>
      <Card.Body>
        <div className="mb-3">
          <div className="text-muted small">
            <i className="bi bi-play-fill me-1"></i>
            <strong>Started:</strong> {formatDateTime(validation.started_at)}
          </div>
          {validation.completed_at && (
            <div className="text-muted small">
              <i className="bi bi-check-circle me-1"></i>
              <strong>Completed:</strong> {formatDateTime(validation.completed_at)}
            </div>
          )}
          {validation.completed_at && (
            <div className="text-muted small">
              <i className="bi bi-clock me-1"></i>
              <strong>Total Duration:</strong> {formatDuration(validation.started_at, validation.completed_at)}
            </div>
          )}
        </div>

        <div className="mt-3">
          {validation.steps.map((step) => (
            <div
              key={step.step}
              className={`border rounded p-3 mb-2 ${isCurrentStep(step) ? "border-primary border-2 bg-light" : ""}`}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-2">
                    <i
                      className={`bi ${getStatusIcon(step.status)} me-2 text-${getStatusBadgeVariant(step.status)}`}
                    ></i>
                    <h6 className="mb-0 text-capitalize me-2">{step.step.replace(/_/g, " ")}</h6>
                    <Badge bg={getStatusBadgeVariant(step.status)}>{step.status}</Badge>
                  </div>

                  <div className="text-muted small">
                    {step.started_at && (
                      <div className="mb-1">
                        <i className="bi bi-play-fill me-1"></i>
                        <strong>Started:</strong> {formatDateTime(step.started_at)}
                      </div>
                    )}
                    {step.completed_at ? (
                      <div className="mb-1">
                        <i className="bi bi-check-circle me-1"></i>
                        <strong>Completed:</strong> {formatDateTime(step.completed_at)}
                      </div>
                    ) : (
                      <div className="mb-1">
                        <i className="bi bi-arrow-repeat me-1"></i>
                        <strong>Updated:</strong> {formatDateTime(validation.updated_at)}
                      </div>
                    )}
                    {step.started_at && (
                      <div className="mb-1">
                        <i className="bi bi-clock me-1"></i>
                        <strong>Duration:</strong> {formatDuration(step.started_at, step.completed_at)}
                      </div>
                    )}
                    {step.completed_by && (
                      <div className="mb-1">
                        <i className="bi bi-person me-1"></i>
                        <strong>Completed by:</strong> {step.completed_by}
                      </div>
                    )}
                  </div>

                  {step.error && (
                    <Alert variant="danger" className="mt-2 mb-0 py-2 small">
                      <i className="bi bi-exclamation-triangle-fill me-2"></i>
                      <strong>Error:</strong> {step.error}
                    </Alert>
                  )}

                  {step.data && Object.keys(step.data).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-muted small" style={{ cursor: "pointer" }}>
                        <i className="bi bi-info-circle me-1"></i>
                        Additional Data
                      </summary>
                      <pre className="mt-2 p-2 bg-light rounded small mb-0" style={{ fontSize: "0.75rem" }}>
                        {JSON.stringify(step.data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasPipeline && !readOnly && onStartPipeline && (
          <div className="d-flex justify-content-center mt-3">
            <Button
              variant="warning"
              onClick={() =>
                onStartPipeline(
                  integrationStatus === "active" ||
                    !!validation.completed_at ||
                    validation.current_step === "pending_for_activation",
                )
              }
              disabled={isPipelineStarting}
            >
              {isPipelineStarting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Validating...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-clockwise me-2"></i>Restart Activation Pipeline
                </>
              )}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default IntegrationActivationPipeline;
